import { useState, useMemo, useEffect, useCallback } from 'react';
import { Patient, TriageTag } from '@/lib/gameData';

type AgeGroup = 'neonate' | 'infant' | 'toddler' | 'preschool' | 'schoolAge' | 'adolescent';

const getAgeGroup = (age: number): AgeGroup => {
  if (age <= 0) return 'neonate';
  if (age === 1) return 'infant';
  if (age <= 3) return 'toddler';
  if (age <= 5) return 'preschool';
  if (age <= 12) return 'schoolAge';
  return 'adolescent';
};

const AVPU_EXAMPLES: Record<AgeGroup, Record<string, string[]>> = {
  neonate: {
    A: ['Eyes open, alert to face', 'Crying appropriately', 'Startles to sound'],
    V: ['Stirs or opens eyes briefly to caregiver\'s voice'],
    P: ['Withdraws limb from heel stick or pinch'],
    Pi: ['Decorticate or decerebrate posturing, no withdrawal'],
  },
  infant: {
    A: ['Eyes open, tracking faces', 'Recognizes caregiver', 'Crying appropriately'],
    V: ['Turns head toward voice, opens eyes to sound'],
    P: ['Withdraws from stimulus, facial grimace with purposeful movement'],
    Pi: ['Abnormal extension or flexion posturing', 'High-pitched cry with no movement'],
  },
  toddler: {
    A: ['Knows their name', 'Says a few words', 'Reaches for caregiver', 'Follows simple commands'],
    V: ['Opens eyes when name is called, may whimper'],
    P: ['Pulls away from pain, cries and reaches for caregiver'],
    Pi: ['Decorticate posturing, stiffening of limbs, no purposeful response'],
  },
  preschool: {
    A: ['Answers simple questions', 'Knows body parts', 'Follows basic 2-step commands'],
    V: ['Opens eyes to voice, may say a word or two'],
    P: ['Withdraws and cries, tries to push stimulus away'],
    Pi: ['Decerebrate posturing, moaning only, no directed movement'],
  },
  schoolAge: {
    A: ['Oriented to person and place', 'Converses normally', 'Follows multi-step instructions'],
    V: ['Opens eyes to voice, responds but confused or slow'],
    P: ['Localizes pain, pushes away stimulus', 'Verbalizes "stop" or "ow"'],
    Pi: ['Abnormal posturing, grimace with no motor response'],
  },
  adolescent: {
    A: ['Fully oriented to person, place, and time', 'Conversational and appropriate'],
    V: ['Opens eyes to voice, mumbles or gives brief verbal response'],
    P: ['Localizes and pushes away pain source', 'Purposeful movement'],
    Pi: ['Decerebrate or decorticate posturing', 'Flaccid with minimal reaction'],
  },
};

type FlowStep = 'initial' | 'post-reposition' | 'post-rescue-breaths' | 'triage-exam';

interface PatientTriageFlowProps {
  patient: Patient;
  onAssignTag: (tag: TriageTag, penalty: number, penaltyMessages: string[]) => void;
  onClose: () => void;
}

const PatientTriageFlow = ({ patient, onAssignTag, onClose }: PatientTriageFlowProps) => {
  const [step, setStep] = useState<FlowStep>('initial');
  const [penalty, setPenalty] = useState(0);
  const [penaltyMessages, setPenaltyMessages] = useState<string[]>([]);
  const [isClosing, setIsClosing] = useState(false);
  const [airwayDone, setAirwayDone] = useState(false);
  const [examDone, setExamDone] = useState(false);

  // Auto-close with transition when patient timer expires
  const handleAnimatedClose = useCallback(() => {
    if (isClosing) return;
    setIsClosing(true);
    setTimeout(() => onClose(), 500);
  }, [isClosing, onClose]);

  useEffect(() => {
    if (patient.timeRemaining <= 0 && !isClosing) {
      handleAnimatedClose();
    }
  }, [patient.timeRemaining, isClosing, handleAnimatedClose]);

  const avpuExample = useMemo(() => {
    if (!patient.isPediatric || !patient.avpuScore) return null;
    const ageGroup = getAgeGroup(patient.age);
    const key = patient.avpuScore === 'Pi' ? 'Pi' : patient.avpuScore;
    const examples = AVPU_EXAMPLES[ageGroup]?.[key];
    if (!examples || examples.length === 0) return null;
    return examples[Math.floor(Math.random() * examples.length)];
  }, [patient.avpuScore, patient.age, patient.isPediatric]);

  const isPeds = patient.isPediatric;
  const timerRatio = patient.timeRemaining / patient.timerDuration;
  const timerPercent = Math.max(0, timerRatio * 100);
  const timerColorClass =
    timerRatio > 0.5 ? 'bg-timer-safe' : timerRatio > 0.25 ? 'bg-timer-warning' : 'bg-timer-danger';

  // RR stays 0 unless patient is breathing or started breathing after intervention
  const isBreathingNow = patient.isBreathing || (airwayDone && patient.breathesAfterReposition) || (step === 'post-rescue-breaths' && patient.respondsToRescueBreaths);
  const displayRR = isBreathingNow
    ? (step === 'post-rescue-breaths' && patient.respondsToRescueBreaths
        ? (patient.rrAfterRescueBreaths ?? patient.vitals.rr)
        : patient.vitals.rr)
    : 0;

  const addPenalty = (pts: number, message: string) => {
    setPenalty(p => p + pts);
    setPenaltyMessages(prev => [...prev, message]);
  };

  // ── Shared handlers ──
  const handleTagGreen = () => {
    let localPenalty = penalty;
    const localMessages = [...penaltyMessages];
    if (!patient.canWalk) { localPenalty += 5; localMessages.push('Patient cannot walk — not a GREEN case!'); }
    onAssignTag('green', localPenalty, localMessages);
  };

  const handleTagBlack = () => {
    let localPenalty = penalty;
    const localMessages = [...penaltyMessages];
    if (step === 'initial' && !patient.isBreathing) {
      localPenalty += 5; localMessages.push(isPeds ? 'Must head-tilt before tagging BLACK!' : 'Must reposition airway before tagging BLACK!');
    }
    if (step === 'initial' && patient.isBreathing) {
      localPenalty += 5; localMessages.push('Patient is breathing — cannot be Expectant');
    }
    if (step === 'post-reposition' && patient.breathesAfterReposition) {
      localPenalty += 5; localMessages.push('Patient breathes after intervention — not BLACK!');
    }
    if (step === 'post-rescue-breaths' && patient.respondsToRescueBreaths) {
      localPenalty += 5; localMessages.push('Patient responds to rescue breaths — not BLACK!');
    }
    onAssignTag('black', localPenalty, localMessages);
  };

  const handleTagRed = () => {
    let localPenalty = penalty;
    const localMessages = [...penaltyMessages];
    if (step === 'initial' && !patient.isBreathing) {
      localPenalty += 5; localMessages.push(isPeds ? 'Must head-tilt before tagging RED!' : 'Address airway before tagging RED!');
    }
    if (step === 'initial' && patient.isBreathing) {
      localPenalty += 5; localMessages.push('Must perform Exam before tagging RED!');
    }
    if (step === 'post-reposition' && !patient.breathesAfterReposition && !isPeds) {
      localPenalty += 5; localMessages.push('Still not breathing after reposition — this is BLACK!');
    }
    onAssignTag('red', localPenalty, localMessages);
  };

  const handleTagYellow = () => {
    let localPenalty = penalty;
    const localMessages = [...penaltyMessages];
    if (step === 'initial' && !patient.isBreathing) {
      localPenalty += 5; localMessages.push(isPeds ? 'Must head-tilt before tagging YELLOW!' : 'Address airway before tagging YELLOW!');
    }
    if (step === 'initial' && patient.isBreathing) {
      localPenalty += 5; localMessages.push('Must perform Exam before tagging YELLOW!');
    }
    onAssignTag('yellow', localPenalty, localMessages);
  };

  const handleTriageExam = () => {
    if (patient.canWalk) addPenalty(5, 'Unnecessary step — skip exam for walking patients');
    if (step === 'initial' && !patient.isBreathing) addPenalty(5, 'Must reposition airway before exam in non-breathing patient');
    if (step === 'post-reposition' && !patient.breathesAfterReposition && !patient.isBreathing) addPenalty(5, 'Unnecessary step — patient not breathing, address airway first');
    if (step === 'post-rescue-breaths' && patient.respondsToRescueBreaths) addPenalty(5, 'Unnecessary step — Tag immediately if rescue breathing is successful');
    if (step === 'post-rescue-breaths' && !patient.respondsToRescueBreaths) addPenalty(5, 'Unnecessary step — patient not breathing after rescue breaths');
    setExamDone(true);
    setStep('triage-exam');
  };

  const handleRepositionAirway = () => {
    if (airwayDone) return;
    setAirwayDone(true);
    if (patient.canWalk) addPenalty(5, 'Unnecessary step — walking patients don\'t need airway intervention');
    else if (patient.isBreathing) addPenalty(5, 'Unnecessary step — breathing patients don\'t need airway intervention');
    
    setStep('post-reposition');
  };

  const handleRescueBreaths = () => {
    if (!patient.hasRadialPulse) {
      addPenalty(5, 'No peripheral pulse — rescue breaths not indicated');
    }
    setStep('post-rescue-breaths');
  };

  // ── Peds RR check ──
  const isAbnormalRR = isPeds
    ? displayRR < 15 || displayRR > 45
    : displayRR > 30;

  const avpuLabel: Record<string, string> = { A: 'Alert', V: 'Verbal', P: 'Pain', Pi: 'Pain (Inappropriate)', U: 'Unresponsive' };

  return (
    <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-500 ${isClosing ? 'bg-transparent' : 'bg-background/80 backdrop-blur-sm'}`} onClick={onClose}>
      <div
        className={`relative w-full max-w-lg bg-card border-2 border-border rounded-xl p-6 space-y-5 transition-all duration-500 ${isClosing ? 'opacity-0 scale-90 translate-y-4' : 'animate-slide-up'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-2xl font-bold font-display text-foreground">
              {patient.name}
              {isPeds && <span className="ml-2 text-sm text-purple-400 font-mono-game">PEDS</span>}
            </h2>
            <p className="text-sm font-mono-game text-muted-foreground">Age {patient.age} • {patient.mechanism}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-2xl leading-none">✕</button>
        </div>

        {/* Timer */}
        <div className="w-full h-3 rounded-full bg-secondary overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${timerColorClass}`} style={{ width: `${timerPercent}%` }} />
        </div>



        {/* ═══ STEP 1: Initial Assessment ═══ */}
        {step === 'initial' && (
          <div className="space-y-4">
            <p className="text-sm font-mono-game text-primary uppercase tracking-wider">
              Initial Assessment
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-secondary rounded-lg p-3 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Walking?</p>
                <p className="text-lg font-bold font-display text-foreground">
                  {patient.canWalk ? 'YES' : 'NO'}
                </p>
              </div>
              <div className="bg-secondary rounded-lg p-3 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Breathing?</p>
                <p className="text-lg font-bold font-display text-foreground">
                  {patient.isBreathing ? 'YES' : 'NO'}
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm font-mono-game text-primary uppercase tracking-wider mb-2">Action or Tag</p>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={handleTriageExam}
                  className="py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-primary text-primary-foreground">
                  Examine
                </button>
                <button onClick={handleRepositionAirway}
                  className="py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-accent text-accent-foreground">
                  {isPeds ? 'Head Tilt / Chin Lift' : 'Reposition Airway'}
                </button>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2">
                <button onClick={handleTagGreen}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-green text-primary-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-green)/0.5)]">
                  MINOR
                </button>
                <button onClick={handleTagYellow}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-yellow text-primary-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-yellow)/0.5)]">
                  DELAYED
                </button>
                <button onClick={handleTagRed}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-red text-destructive-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-red)/0.5)]">
                  IMMEDIATE
                </button>
                <button onClick={handleTagBlack}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-black text-foreground border border-foreground/30 hover:shadow-[0_0_20px_hsl(var(--foreground)/0.2)]">
                  EXPECTANT
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 1b: After Head-Tilt / Reposition ═══ */}
        {step === 'post-reposition' && (
          <div className="space-y-4">
            <p className="text-sm font-mono-game text-primary uppercase tracking-wider">
              {isPeds ? 'After Head Tilt / Chin Lift' : 'After Airway Reposition'}
            </p>

            {/* Peds + not breathing after reposition: show breathing + pulse side-by-side */}
            {isPeds && !patient.isBreathing && !patient.breathesAfterReposition ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-secondary rounded-lg p-4 text-center">
                  <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Breathing?</p>
                  <p className="text-xl font-bold font-display text-destructive">NO</p>
                  <p className="text-[10px] font-mono-game text-muted-foreground mt-1">Still not breathing</p>
                </div>
                <div className="bg-secondary rounded-lg p-4 text-center">
                  <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Peripheral Pulse?</p>
                  <p className={`text-xl font-bold font-display ${patient.hasRadialPulse ? 'text-green-500' : 'text-destructive'}`}>
                    {patient.hasRadialPulse ? 'Present' : 'Absent'}
                  </p>
                </div>
              </div>
            ) : !patient.isBreathing && !patient.breathesAfterReposition ? (
              <div className="bg-secondary rounded-lg p-4 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Patient now breathing?</p>
                <p className="text-xl font-bold font-display text-destructive">NO — Still not breathing</p>
              </div>
            ) : (
              <div className="bg-secondary rounded-lg p-4 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Patient now breathing?</p>
                <p className="text-xl font-bold font-display text-foreground">
                  {patient.isBreathing
                    ? 'Patient is already breathing'
                    : 'YES — Breathing resumed'}
                </p>
              </div>
            )}



            {/* Triage Exam vitals shown automatically */}
            {((patient.breathesAfterReposition && !patient.isBreathing) || examDone) && (
              <>
                <p className="text-sm font-mono-game text-primary uppercase tracking-wider">
                  Exam Findings {isPeds && '(JumpSTART)'}
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-secondary rounded-lg p-2.5 text-center">
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Resp. Rate</p>
                    <p className="text-base font-bold font-mono-game text-foreground">
                      {displayRR}/min
                    </p>
                  </div>
                  <div className="bg-secondary rounded-lg p-2.5 text-center">
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Radial Pulse</p>
                    <p className="text-base font-bold font-display text-foreground">
                      {patient.hasRadialPulse ? 'Present' : 'Absent'}
                    </p>
                  </div>
                  <div className="bg-secondary rounded-lg p-2.5 text-center">
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Cap Refill</p>
                    <p className="text-base font-bold font-mono-game text-foreground">
                      {patient.hasRadialPulse ? `${patient.capRefill}s` : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-secondary rounded-lg p-2.5 text-center">
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Walking?</p>
                    <p className="text-base font-bold font-display text-foreground">
                      {patient.canWalk ? 'YES' : 'NO'}
                    </p>
                  </div>
                  <div className="col-span-2 bg-secondary rounded-lg p-2.5 text-center break-words">
                    {isPeds ? (
                      <>
                        <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">AVPU</p>
                        {avpuExample ? (
                          <p className="text-sm font-bold font-display text-foreground break-words">{avpuExample}</p>
                        ) : (
                          <p className="text-base font-bold font-display text-foreground">
                            {patient.avpuScore} — {avpuLabel[patient.avpuScore]}
                          </p>
                        )}
                      </>
                    ) : (
                      <>
                        <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Follows Commands</p>
                        <p className="text-base font-bold font-display text-foreground">
                          {patient.followsCommands ? 'YES' : 'NO'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}

            <div>
              <p className="text-sm font-mono-game text-primary uppercase tracking-wider mb-2">Action or Tag</p>
              {/* Breathing patient: full-width Examine */}
              {patient.isBreathing && (
                <button onClick={handleTriageExam}
                  className="w-full py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-primary text-primary-foreground mb-2">
                  Examine
                </button>
              )}
              {/* Peds non-breathing with pulse: Examine + Rescue Breaths */}
              {isPeds && !patient.breathesAfterReposition && patient.hasRadialPulse && (
                <div className={`grid gap-2 mb-2 ${!examDone ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {!examDone && (
                    <button onClick={handleTriageExam}
                      className="py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-primary text-primary-foreground">
                      Examine
                    </button>
                  )}
                  <button onClick={handleRescueBreaths}
                    className="py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-purple-600 text-white hover:bg-purple-500">
                    Give 5 Rescue Breaths
                  </button>
                </div>
              )}
              {/* Peds non-breathing without pulse: Examine distractor */}
              {isPeds && !patient.breathesAfterReposition && !patient.hasRadialPulse && !patient.isBreathing && (
                <div className={`grid gap-2 mb-2 ${!examDone ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {!examDone && (
                    <button onClick={handleTriageExam}
                      className="py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-primary text-primary-foreground">
                      Examine
                    </button>
                  )}
                  <button onClick={handleRescueBreaths}
                    className="py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-purple-600 text-white hover:bg-purple-500">
                    Give 5 Rescue Breaths
                  </button>
                </div>
              )}
              {/* Adult non-breathing after reposition: Examine distractor (penalized) */}
              {!isPeds && !patient.isBreathing && !patient.breathesAfterReposition && !examDone && (
                <button onClick={handleTriageExam}
                  className="w-full py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-primary text-primary-foreground mb-2">
                  Examine
                </button>
              )}
              <div className="grid grid-cols-4 gap-2">
                <button onClick={handleTagGreen}
                   className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-green text-primary-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-green)/0.5)]">
                   MINOR
                </button>
                <button onClick={handleTagYellow}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-yellow text-primary-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-yellow)/0.5)]">
                  DELAYED
                </button>
                <button onClick={handleTagRed}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-red text-destructive-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-red)/0.5)]">
                  IMMEDIATE
                </button>
                <button onClick={handleTagBlack}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-black text-foreground border border-foreground/30 hover:shadow-[0_0_20px_hsl(var(--foreground)/0.2)]">
                  EXPECTANT
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 1c: After Rescue Breaths (Peds only) ═══ */}
        {step === 'post-rescue-breaths' && (
          <div className="space-y-4">
            <p className="text-sm font-mono-game text-primary uppercase tracking-wider">
              After 5 Rescue Breaths (JumpSTART)
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-secondary rounded-lg p-4 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Breathing?</p>
                <p className={`text-xl font-bold font-display ${patient.respondsToRescueBreaths ? 'text-foreground' : 'text-destructive'}`}>
                  {patient.respondsToRescueBreaths ? 'YES' : 'NO'}
                </p>
                <p className="text-[10px] font-mono-game text-muted-foreground mt-1">
                  {patient.respondsToRescueBreaths ? 'Breathing resumed' : 'Still not breathing'}
                </p>
              </div>
              <div className="bg-secondary rounded-lg p-4 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Peripheral Pulse?</p>
                <p className={`text-xl font-bold font-display ${patient.hasRadialPulse ? 'text-green-500' : 'text-destructive'}`}>
                  {patient.hasRadialPulse ? 'Present' : 'Absent'}
                </p>
              </div>
            </div>

            {/* Triage Exam vitals shown automatically if breathing */}
            {patient.respondsToRescueBreaths && (
              <>
                <p className="text-sm font-mono-game text-primary uppercase tracking-wider">
                  Exam Findings (JumpSTART)
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-secondary rounded-lg p-2.5 text-center">
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Resp. Rate</p>
                    <p className="text-base font-bold font-mono-game text-foreground">
                      {patient.rrAfterRescueBreaths ?? patient.vitals.rr}/min
                    </p>
                  </div>
                  <div className="bg-secondary rounded-lg p-2.5 text-center">
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Radial Pulse</p>
                    <p className="text-base font-bold font-display text-foreground">
                      {patient.hasRadialPulse ? 'Present' : 'Absent'}
                    </p>
                  </div>
                  <div className="bg-secondary rounded-lg p-2.5 text-center">
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Cap Refill</p>
                    <p className="text-base font-bold font-mono-game text-foreground">
                      {patient.hasRadialPulse ? `${patient.capRefill}s` : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="bg-secondary rounded-lg p-2.5 text-center">
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Walking?</p>
                    <p className="text-base font-bold font-display text-foreground">
                      {patient.canWalk ? 'YES' : 'NO'}
                    </p>
                  </div>
                  <div className="col-span-2 bg-secondary rounded-lg p-2.5 text-center break-words">
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">AVPU</p>
                    {avpuExample ? (
                      <p className="text-sm font-bold font-display text-foreground break-words">{avpuExample}</p>
                    ) : (
                      <p className="text-base font-bold font-display text-foreground">
                        {patient.avpuScore} — {avpuLabel[patient.avpuScore]}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}

            <div>
              <p className="text-sm font-mono-game text-primary uppercase tracking-wider mb-2">Action or Tag</p>
              {patient.respondsToRescueBreaths && !examDone && (
                <button onClick={handleTriageExam}
                  className="w-full py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-primary text-primary-foreground mb-2">
                  Examine
                </button>
              )}
              <div className="grid grid-cols-4 gap-2">
                <button onClick={handleTagGreen}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-green text-primary-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-green)/0.5)]">
                  MINOR
                </button>
                <button onClick={handleTagYellow}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-yellow text-primary-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-yellow)/0.5)]">
                  DELAYED
                </button>
                <button onClick={handleTagRed}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-red text-destructive-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-red)/0.5)]">
                  IMMEDIATE
                </button>
                <button onClick={handleTagBlack}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-black text-foreground border border-foreground/30 hover:shadow-[0_0_20px_hsl(var(--foreground)/0.2)]">
                  EXPECTANT
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 2: Triage Exam ═══ */}
        {step === 'triage-exam' && (
          <div className="space-y-4">
            {/* Show reposition result context if airway was already done */}
            {airwayDone && !patient.isBreathing && (
              <>
                <p className="text-sm font-mono-game text-primary uppercase tracking-wider">
                  {isPeds ? 'After Head Tilt / Chin Lift' : 'After Airway Reposition'}
                </p>
                <div className="bg-secondary rounded-lg p-4 text-center">
                  <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Patient now breathing?</p>
                  <p className={`text-xl font-bold font-display ${patient.breathesAfterReposition ? 'text-foreground' : 'text-destructive'}`}>
                    {patient.breathesAfterReposition ? 'YES — Breathing resumed' : 'NO — Still not breathing'}
                  </p>
                </div>
              </>
            )}

            <p className="text-sm font-mono-game text-primary uppercase tracking-wider">
              Exam Findings {isPeds && '(JumpSTART)'}
            </p>

            <div className="grid grid-cols-3 gap-2">
              <div className="bg-secondary rounded-lg p-2.5 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Resp. Rate</p>
                <p className="text-base font-bold font-mono-game text-foreground">
                  {displayRR}/min
                </p>
              </div>
              <div className="bg-secondary rounded-lg p-2.5 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Radial Pulse</p>
                <p className="text-base font-bold font-display text-foreground">
                  {patient.hasRadialPulse ? 'Present' : 'Absent'}
                </p>
              </div>
              <div className="bg-secondary rounded-lg p-2.5 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Cap Refill</p>
                <p className="text-base font-bold font-mono-game text-foreground">
                  {patient.hasRadialPulse ? `${patient.capRefill}s` : 'N/A'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-secondary rounded-lg p-2.5 text-center">
                <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Walking?</p>
                <p className="text-base font-bold font-display text-foreground">
                  {patient.canWalk ? 'YES' : 'NO'}
                </p>
              </div>
              <div className="col-span-2 bg-secondary rounded-lg p-2.5 text-center break-words">
                {isPeds ? (
                  <>
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">AVPU</p>
                    {avpuExample ? (
                      <p className="text-sm font-bold font-display text-foreground break-words">{avpuExample}</p>
                    ) : (
                      <p className="text-base font-bold font-display text-foreground">
                        {patient.avpuScore} — {avpuLabel[patient.avpuScore]}
                      </p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-xs font-mono-game text-muted-foreground uppercase mb-1">Follows Commands</p>
                    <p className="text-base font-bold font-display text-foreground">
                      {patient.followsCommands ? 'YES' : 'NO'}
                    </p>
                  </>
                )}
              </div>
            </div>

            <div>
              <p className="text-sm font-mono-game text-primary uppercase tracking-wider mb-2">Action or Tag</p>
              {!airwayDone && (
                <button onClick={handleRepositionAirway}
                  className="w-full mb-2 py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-accent text-accent-foreground hover:shadow-[0_0_20px_hsl(var(--accent)/0.5)]">
                  {isPeds ? 'Head Tilt / Chin Lift' : 'Reposition Airway'}
                </button>
              )}
              {isPeds && airwayDone && !patient.breathesAfterReposition && patient.hasRadialPulse && !patient.isBreathing && (
                <button onClick={handleRescueBreaths}
                  className="w-full mb-2 py-3 px-2 md:px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-purple-600 text-white hover:bg-purple-500">
                  Give 5 Rescue Breaths
                </button>
              )}
              <div className="grid grid-cols-4 gap-2">
                <button onClick={handleTagGreen}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-green text-primary-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-green)/0.5)]">
                  MINOR
                </button>
                <button onClick={handleTagYellow}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-yellow text-primary-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-yellow)/0.5)]">
                  DELAYED
                </button>
                <button onClick={handleTagRed}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-red text-destructive-foreground hover:shadow-[0_0_20px_hsl(var(--emergency-red)/0.5)]">
                  IMMEDIATE
                </button>
                <button onClick={handleTagBlack}
                  className="py-3 px-4 rounded-lg font-display font-bold text-xs md:text-sm uppercase tracking-wide text-center flex items-center justify-center transition-all hover:scale-[1.03] bg-emergency-black text-foreground border border-foreground/30 hover:shadow-[0_0_20px_hsl(var(--foreground)/0.2)]">
                  EXPECTANT
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PatientTriageFlow;
